import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, KeyRound, Clock, CalendarCheck, ClipboardList, Loader2, Check, X, Navigation } from "lucide-react";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL } from "../lib/permisos";
import { getFabHabilitado, setFabHabilitado } from "../components/QuickNavFab";

const TIPO_LABEL: Record<string, string> = {
  creada: "Creó reserva",
  confirmada: "Confirmó reserva",
  cancelada: "Canceló reserva",
  editada: "Editó reserva",
  checkin: "Check-in",
  marcada_no_show: "Marcó no-show",
  atendida: "Marcó atendida",
};

function fmtDateTime(s?: string) {
  if (!s) return "—";
  return new Date(s).toLocaleString("es-VE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function PerfilPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // Nombre / teléfono
  const [nombre, setNombre] = useState(user?.fullName ?? "");
  const [telefono, setTelefono] = useState(user?.telefono ?? "");
  const [savingNombre, setSavingNombre] = useState(false);
  const [savedNombre, setSavedNombre] = useState(false);
  const [errorNombre, setErrorNombre] = useState<string | null>(null);

  // Password
  const [claveActual, setClaveActual] = useState("");
  const [claveNueva, setClaveNueva] = useState("");
  const [claveRepeat, setClaveRepeat] = useState("");
  const [savingPass, setSavingPass] = useState(false);
  const [msgPass, setMsgPass] = useState<{ ok: boolean; txt: string } | null>(null);

  // Actividad
  const [actividad, setActividad] = useState<{
    reservaEvents: any[];
    checklistDone: any[];
  } | null>(null);
  const [tareasOp, setTareasOp] = useState<any[]>([]);
  const [checklistPendiente, setChecklistPendiente] = useState<any[]>([]);
  const [loadingAct, setLoadingAct] = useState(true);
  const [tab, setTab] = useState<"datos" | "actividad" | "checklist">("datos");

  // Preferencias UI (locales al dispositivo)
  const [fabActivo, setFabActivo] = useState<boolean>(() => getFabHabilitado());

  useEffect(() => {
    setNombre(user?.fullName ?? "");
    setTelefono(user?.telefono ?? "");
  }, [user]);

  useEffect(() => {
    if (tab === "actividad") {
      setLoadingAct(true);
      Promise.all([
        fetch("/api/v1/users/me/actividad", { headers }).then(r => r.json()),
        fetch("/api/v1/tareas", { headers }).then(r => r.ok ? r.json() : []),
      ])
        .then(([act, tareas]) => {
          setActividad(act);
          setTareasOp(Array.isArray(tareas) ? tareas : []);
        })
        .catch(() => setActividad({ reservaEvents: [], checklistDone: [] }))
        .finally(() => setLoadingAct(false));
    }
    if (tab === "checklist") {
      setLoadingAct(true);
      fetch("/api/v1/eventos/mis-tareas", { headers })
        .then(r => r.ok ? r.json() : [])
        .then(d => setChecklistPendiente(Array.isArray(d) ? d : []))
        .catch(() => setChecklistPendiente([]))
        .finally(() => setLoadingAct(false));
    }
  }, [tab, token]);

  async function guardarNombre() {
    if (!nombre.trim()) { setErrorNombre("El nombre no puede estar vacío"); return; }
    setSavingNombre(true); setErrorNombre(null);
    try {
      const r = await fetch("/api/v1/users/me", {
        method: "PATCH", headers, body: JSON.stringify({ fullName: nombre.trim(), telefono: telefono.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).message ?? "Error");
      setSavedNombre(true);
      setTimeout(() => setSavedNombre(false), 2000);
    } catch (e: any) {
      setErrorNombre(e.message);
    } finally { setSavingNombre(false); }
  }

  async function cambiarClave() {
    if (!claveActual) { setMsgPass({ ok: false, txt: "Ingresá tu contraseña actual" }); return; }
    if (claveNueva.length < 6) { setMsgPass({ ok: false, txt: "La nueva contraseña debe tener al menos 6 caracteres" }); return; }
    if (claveNueva !== claveRepeat) { setMsgPass({ ok: false, txt: "Las contraseñas no coinciden" }); return; }
    setSavingPass(true); setMsgPass(null);
    try {
      const r = await fetch("/api/v1/users/me/password", {
        method: "PATCH", headers, body: JSON.stringify({ claveActual, claveNueva }),
      });
      if (!r.ok) throw new Error((await r.json()).message ?? "Error");
      setMsgPass({ ok: true, txt: "✓ Contraseña actualizada" });
      setClaveActual(""); setClaveNueva(""); setClaveRepeat("");
    } catch (e: any) {
      setMsgPass({ ok: false, txt: e.message });
    } finally { setSavingPass(false); }
  }

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Header */}
      <header className="bg-white border-b border-ink-200">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-ink-400 hover:text-ink-700">
            <ArrowLeft className="size-5" />
          </button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
              {user?.fullName?.[0]?.toUpperCase() ?? "U"}
            </div>
            <div>
              <h1 className="font-bold text-ink-800">{user?.fullName}</h1>
              <p className="text-xs text-ink-400">{ROLE_LABEL[user?.role ?? ""] ?? user?.role} · {user?.email}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-6 flex gap-1 border-t border-ink-100">
          {[
            { id: "datos", label: "Mis datos", icon: Save },
            { id: "actividad", label: "Mi actividad", icon: Clock },
            { id: "checklist", label: "Mis checklists", icon: ClipboardList },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id as any)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-brand-600 text-brand-700" : "border-transparent text-ink-500 hover:text-ink-700"}`}
            >
              <t.icon className="size-4" />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-5">

        {/* ── TAB DATOS ── */}
        {tab === "datos" && (
          <>
            {/* Cambiar nombre */}
            <div className="card p-5 space-y-4">
              <h2 className="font-semibold text-ink-700 text-sm uppercase tracking-wide">Nombre y perfil</h2>
              <div>
                <label className="block text-xs text-ink-500 mb-1">Nombre completo</label>
                <input
                  className="input w-full"
                  value={nombre}
                  onChange={e => { setNombre(e.target.value); setSavedNombre(false); }}
                  onKeyDown={e => e.key === "Enter" && guardarNombre()}
                  placeholder="Tu nombre completo"
                />
                {errorNombre && <p className="text-xs text-red-500 mt-1">{errorNombre}</p>}
              </div>
              <div>
                <label className="block text-xs text-ink-500 mb-1">Teléfono (WhatsApp)</label>
                <div className="flex gap-2">
                  <input
                    className="input flex-1"
                    value={telefono}
                    onChange={e => { setTelefono(e.target.value); setSavedNombre(false); }}
                    onKeyDown={e => e.key === "Enter" && guardarNombre()}
                    placeholder="0412-1234567"
                  />
                  <button
                    onClick={guardarNombre}
                    disabled={savingNombre || (nombre === user?.fullName && telefono === (user?.telefono ?? ""))}
                    className="btn-primary text-sm px-4 disabled:opacity-50 flex items-center gap-2"
                  >
                    {savingNombre ? <Loader2 className="size-4 animate-spin" /> :
                     savedNombre ? <><Check className="size-4" /> Guardado</> :
                     <><Save className="size-4" /> Guardar</>}
                  </button>
                </div>
                <p className="text-[11px] text-ink-400 mt-1">
                  Se usa para avisarte por WhatsApp cuando te asignen una tarea de un evento.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="block text-xs text-ink-500 mb-1">Email</label>
                  <p className="text-ink-700 bg-ink-50 rounded-lg px-3 py-2">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-xs text-ink-500 mb-1">Rol</label>
                  <p className="text-ink-700 bg-ink-50 rounded-lg px-3 py-2">{ROLE_LABEL[user?.role ?? ""] ?? user?.role}</p>
                </div>
              </div>
            </div>

            {/* Cambiar contraseña */}
            <div className="card p-5 space-y-4">
              <h2 className="font-semibold text-ink-700 text-sm uppercase tracking-wide flex items-center gap-2">
                <KeyRound className="size-4" /> Cambiar contraseña
              </h2>
              {msgPass && (
                <div className={`text-sm px-3 py-2 rounded-lg border ${msgPass.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                  {msgPass.txt}
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-ink-500 mb-1">Contraseña actual</label>
                  <input type="password" className="input w-full" value={claveActual}
                    onChange={e => setClaveActual(e.target.value)} placeholder="Tu contraseña actual" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">Nueva contraseña</label>
                    <input type="password" className="input w-full" value={claveNueva}
                      onChange={e => setClaveNueva(e.target.value)} placeholder="Mínimo 6 caracteres" />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">Repetir nueva</label>
                    <input type="password" className="input w-full" value={claveRepeat}
                      onChange={e => setClaveRepeat(e.target.value)} placeholder="Confirmar" />
                  </div>
                </div>
                <button onClick={cambiarClave} disabled={savingPass}
                  className="btn-primary text-sm disabled:opacity-50 flex items-center gap-2">
                  {savingPass ? <><Loader2 className="size-4 animate-spin" /> Cambiando…</> :
                   <><KeyRound className="size-4" /> Cambiar contraseña</>}
                </button>
              </div>
            </div>

            {/* Preferencias de interfaz (locales al dispositivo) */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3 bg-indigo-50/50 border-b border-indigo-100 flex items-center gap-2">
                <Navigation className="size-4 text-indigo-600" />
                <h3 className="font-semibold text-ink-800 text-sm">Preferencias de interfaz</h3>
                <span className="ml-auto text-[10px] text-ink-400 uppercase tracking-wide">
                  Por dispositivo
                </span>
              </div>
              <div className="p-5 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fabActivo}
                    onChange={(e) => {
                      setFabActivo(e.target.checked);
                      setFabHabilitado(e.target.checked);
                    }}
                    className="size-5 accent-indigo-600 mt-0.5"
                  />
                  <div>
                    <p className="font-medium text-ink-800 text-sm">
                      Botón flotante de navegación rápida
                    </p>
                    <p className="text-xs text-ink-500 mt-0.5">
                      Botón pequeño en la esquina inferior derecha con accesos a Inicio,
                      Calendario, Reservas y "Ir arriba". Se oculta automáticamente al hacer
                      scroll para no estorbar.
                    </p>
                  </div>
                </label>
                <p className="text-[11px] text-ink-400 italic pl-8">
                  Esta preferencia se guarda en este dispositivo (no en tu cuenta).
                </p>
              </div>
            </div>
          </>
        )}

        {/* ── TAB ACTIVIDAD ── */}
        {tab === "actividad" && (
          <>
            {/* Avisos y tareas operativas */}
            {!loadingAct && (tareasOp.length > 0) && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 bg-violet-50 border-b border-violet-100 flex items-center gap-2">
                  <ClipboardList className="size-4 text-violet-600" />
                  <h2 className="font-semibold text-ink-700 text-sm">Avisos y tareas operativas</h2>
                  <span className="ml-auto text-xs text-ink-400">({tareasOp.length})</span>
                </div>
                <div className="divide-y divide-ink-50">
                  {tareasOp.map((t: any, i: number) => {
                    const prioColor: Record<string, string> = {
                      urgente: "bg-red-100 text-red-700", alta: "bg-orange-100 text-orange-700",
                      normal: "bg-violet-100 text-violet-700", baja: "bg-slate-100 text-slate-600",
                    };
                    return (
                      <div key={i} className="flex items-start gap-3 px-5 py-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${t.estado === 'completada' ? 'bg-green-400' : 'bg-violet-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-medium text-ink-800 ${t.estado === 'completada' ? 'line-through opacity-60' : ''}`}>{t.titulo}</p>
                          <p className="text-xs text-ink-400">
                            De: {t.creadoPorNombre} ·
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${prioColor[t.prioridad] ?? prioColor.normal}`}>
                              {t.prioridad}
                            </span>
                          </p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${t.estado === 'completada' ? 'bg-green-100 text-green-700' : 'bg-violet-100 text-violet-700'}`}>
                          {t.estado}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {loadingAct ? (
              <div className="flex items-center justify-center h-32 text-ink-400">
                <Loader2 className="size-5 animate-spin mr-2" /> Cargando actividad…
              </div>
            ) : (
              <>
                {/* Historial de reservas */}
                <div className="card overflow-hidden">
                  <div className="px-5 py-3 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
                    <CalendarCheck className="size-4 text-brand-600" />
                    <h2 className="font-semibold text-ink-700 text-sm">Acciones en reservas</h2>
                    <span className="ml-auto text-xs text-ink-400">({actividad?.reservaEvents.length ?? 0})</span>
                  </div>
                  {(actividad?.reservaEvents ?? []).length === 0 ? (
                    <p className="px-5 py-6 text-sm text-ink-400 text-center">Sin acciones registradas</p>
                  ) : (
                    <div className="divide-y divide-ink-50">
                      {actividad?.reservaEvents.map((e, i) => (
                        <div key={i} className="flex items-start gap-3 px-5 py-3 hover:bg-ink-50 transition-colors">
                          <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center shrink-0">
                            <CalendarCheck className="size-3.5 text-brand-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-ink-700">
                                {TIPO_LABEL[e.tipo] ?? e.tipo}
                              </span>
                              <span className="text-xs text-ink-500">#{e.reservaId} · {e.nombreCliente}</span>
                            </div>
                            <p className="text-xs text-ink-400">
                              {e.fechaReserva} {e.horaReserva?.slice(0,5)} · {e.sucursalNombre}
                            </p>
                            {e.notas && (
                              <p className="text-[11px] text-ink-400 italic mt-0.5 truncate max-w-sm">{e.notas}</p>
                            )}
                          </div>
                          <span className="text-[10px] text-ink-300 shrink-0">{fmtDateTime(e.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Checklist completado */}
                <div className="card overflow-hidden">
                  <div className="px-5 py-3 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
                    <ClipboardList className="size-4 text-emerald-600" />
                    <h2 className="font-semibold text-ink-700 text-sm">Tareas de checklist completadas</h2>
                    <span className="ml-auto text-xs text-ink-400">({actividad?.checklistDone.length ?? 0})</span>
                  </div>
                  {(actividad?.checklistDone ?? []).length === 0 ? (
                    <p className="px-5 py-6 text-sm text-ink-400 text-center">Sin tareas completadas asignadas</p>
                  ) : (
                    <div className="divide-y divide-ink-50">
                      {actividad?.checklistDone.map((c, i) => (
                        <div key={i} className="flex items-start gap-3 px-5 py-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                            <Check className="size-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-ink-800">{c.itemText}</p>
                            <p className="text-xs text-ink-400">📅 {c.eventoNombre} · {c.fecha}</p>
                          </div>
                          {c.completadoAt && (
                            <span className="text-[10px] text-ink-300 shrink-0">{fmtDateTime(c.completadoAt)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ── TAB MIS CHECKLISTS ── */}
        {tab === "checklist" && (
          <div className="space-y-4">
            <div className="card overflow-hidden">
              <div className="px-5 py-3 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
                <ClipboardList className="size-4 text-brand-600" />
                <h2 className="font-semibold text-ink-700 text-sm">Tareas de eventos asignadas a mí</h2>
                <span className="ml-auto text-xs text-ink-400">
                  {loadingAct ? "…" : `${checklistPendiente.length} pendiente${checklistPendiente.length !== 1 ? 's' : ''}`}
                </span>
              </div>
              {loadingAct ? (
                <div className="px-5 py-8 text-center text-ink-400 text-sm">Cargando…</div>
              ) : checklistPendiente.length === 0 ? (
                <div className="px-5 py-8 text-center text-ink-400 text-sm">
                  ✅ Sin tareas pendientes de checklist asignadas
                </div>
              ) : (
                <div className="divide-y divide-ink-50">
                  {checklistPendiente.map((c: any, i: number) => (
                    <div key={i} className="flex items-start gap-3 px-5 py-3 hover:bg-ink-50">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                        c.itemEstado === 'culminado' ? 'bg-green-500' :
                        c.itemEstado === 'en_proceso' ? 'bg-blue-500' :
                        c.itemEstado === 'urgente' ? 'bg-red-500' : 'bg-amber-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-800">{c.itemText}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-ink-500">
                            📅 {c.eventoNombre}
                          </span>
                          {c.fecha && (
                            <span className="text-xs text-ink-400">
                              · {new Date(c.fecha + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          {c.fechaEntrega && (
                            <span className="text-xs text-amber-600 font-medium">
                              · Entrega: {fmtDateTime(c.fechaEntrega)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 font-medium ${
                        c.itemEstado === 'culminado' ? 'bg-green-100 text-green-700' :
                        c.itemEstado === 'en_proceso' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {c.itemEstado ?? 'pendiente'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-ink-400 text-center">
              Para asignarte tareas, ve a un evento privado → sección Checklist → selector de usuario
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
